import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { FilmSession } from '../models/film-session';
import { FilmSessionService } from './film-session.service';
import { FilmSocketService } from './film-socket.service';
import { LocalStorageService } from './local-storage.service';

/**
 * The live film session running on the signed-in user's current team, if they're invited to it, for the join
 * banner. It checks on load, on team switches and on every /film reconnect, and follows the socket's live and ended
 * notices in between.
 */
@Injectable({
  providedIn: 'root'
})
export class LiveSessionStatusService implements OnDestroy {

  private readonly live = new BehaviorSubject<FilmSession>(null);
  readonly live$: Observable<FilmSession> = this.live.asObservable();
  /** Sessions this user is done with: they ended, or the user was removed. A late lookup never brings them back. */
  private readonly closed = new Set<string>();
  private subscriptions = new Subscription();
  private lookup = 0;

  constructor(
    private localStorageService: LocalStorageService,
    private filmSessionService: FilmSessionService,
    private filmSocket: FilmSocketService
  ) {
    this.subscriptions.add(this.localStorageService.listenForUserUpdate().subscribe(() => this.refresh()));
    this.subscriptions.add(this.filmSocket.connection$.pipe(filter(connected => connected)).subscribe(() => this.refresh()));
    this.subscriptions.add(this.filmSocket.sessionLive$.subscribe(session => {
      if (session?.teamid === this.currentTeamid() && !this.closed.has(session._id)) this.live.next(session);
    }));
    this.subscriptions.add(this.filmSocket.sessionEnded$.subscribe(session => {
      if (session?._id) this.markClosed(session._id);
    }));
    this.refresh();
  }

  get session(): FilmSession {
    return this.live.value;
  }

  /** The room saw the session end (or the user removed from it), so the join banner stays away. */
  markClosed(sessionId: string): void {
    this.closed.add(sessionId);
    if (this.live.value?._id === sessionId) this.live.next(null);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private currentTeamid(): string {
    return this.localStorageService.getUser()?.currentUserRole?.teamid;
  }

  async refresh(): Promise<void> {
    const lookup = ++this.lookup;
    const user = this.localStorageService.getUser();
    const teamid = user?.currentUserRole?.teamid;
    if (!user?.team?.liveFilmSessionsEnabled || !teamid) {
      this.live.next(null);
      return;
    }
    const res = await this.filmSessionService.getLive(teamid).catch(() => null);
    // A newer lookup (e.g. after a team switch) wins.
    if (lookup !== this.lookup) return;
    const session = res?.success ? res.result : null;
    this.live.next(session && !this.closed.has(session._id) ? session : null);
  }
}
